import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, LayoutDashboard, Sparkles, Map as MapIcon, Bell,
  Wrench, ClipboardList, FileText, AlertOctagon, Users, HardHat, MapPin, Tags,
  Package, Building, Boxes, Receipt, TrendingUp, Truck, Undo2,
  Building2, ClipboardCheck,
  ListChecks,
  School, IdCard, Boxes as EquipIcon, Activity,
  ScrollText, RefreshCcw, ShieldCheck,
  BarChart3, FileSpreadsheet, FileDown, FileUp,
  Settings,
  ChevronDown, Upload, Moon,
} from 'lucide-react';

/* Nav model — SUPPLY-ONLY mode (Feb 2026 pivot).
 * Non-Supply modules (Maintenance, Construction, Readiness, Field Visits, etc.)
 * remain fully functional and routed in App.js — they are only hidden from the
 * sidebar so the visible product is a focused Supply Operations experience.
 * To restore, re-add their entries below.
 */
const NAV = [
  { kind: 'leaf', id: 'supply-home', label: 'لوحة القيادة',        icon: LayoutDashboard, path: '/',                testid: 'nav-supply-home' },
  { kind: 'leaf', id: 'suppliers',   label: 'الموردون',              icon: Building,         path: '/suppliers-list',  testid: 'nav-suppliers-list' },
  { kind: 'leaf', id: 'schools',     label: 'المدارس',               icon: School,           path: '/schools',         testid: 'nav-schools' },
  { kind: 'leaf', id: 'administrations', label: 'الإدارات التعليمية', icon: Building2,      path: '/administrations', testid: 'nav-administrations' },
  { kind: 'leaf', id: 'items',       label: 'الأصناف',               icon: Boxes,            path: '/items-list',      testid: 'nav-items-list' },
  { kind: 'leaf', id: 'map',         label: 'الخريطة',               icon: MapIcon,          path: '/map',             testid: 'nav-map' },
  { kind: 'leaf', id: 'reports',     label: 'التحليلات والتقارير',   icon: BarChart3,        path: '/reports',         testid: 'nav-reports' },
  { kind: 'leaf', id: 'data-quality', label: 'جودة البيانات',        icon: ShieldCheck,      path: '/data-quality',    testid: 'nav-data-quality' },
  { kind: 'leaf', id: 'imports',     label: 'مركز الاستيراد',        icon: Upload,           path: '/supply/dynamic-import', testid: 'nav-imports' },
  { kind: 'leaf', id: 'settings',    label: 'الإعدادات',              icon: Settings,         path: '/settings',        testid: 'nav-settings' },
];

/* Given the current pathname, which section (if any) contains it? */
function findActiveSection(pathname) {
  for (const item of NAV) {
    if (item.kind === 'section' && item.children?.some((c) => c.path.split('?')[0].split('#')[0] === pathname)) {
      return item.id;
    }
  }
  return null;
}

export default function Sidebar() {
  const nav = useNavigate();
  const { pathname } = useLocation();

  const initialOpen = useMemo(() => findActiveSection(pathname) || 'home', [pathname]);
  const [openId, setOpenId] = useState(initialOpen);

  useEffect(() => {
    const s = findActiveSection(pathname);
    if (s) setOpenId(s);
  }, [pathname]);

  const isPathActive = (path) => {
    const clean = path.split('?')[0].split('#')[0];
    return clean === '/' ? pathname === '/' : pathname === clean;
  };
  const toggleSection = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <aside dir="rtl" className="side w-[240px] shrink-0 h-screen sticky top-0 flex flex-col py-5 px-3">
      {/* Brand */}
      <div className="flex items-center gap-3 pb-5 mb-4 border-b hair" data-testid="sidebar-brand">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center relative overflow-hidden"
             style={{
               background: 'linear-gradient(135deg, rgba(59,130,246,0.45) 0%, rgba(14,165,233,0.22) 100%)',
               border: '1px solid rgba(59,130,246,0.45)',
               boxShadow: '0 6px 18px -8px rgba(59,130,246,0.55)',
             }}>
          <span className="text-white font-extrabold text-[15px] tracking-tight">TBC</span>
        </div>
        <div className="min-w-0">
          <div className="text-[13px] text-slate-100 font-bold leading-tight">شركة تطوير المباني</div>
          <div className="text-[10.5px] text-slate-400 mt-0.5">Operations Command</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto pr-1 space-y-1">
        {NAV.map((item) => {
          const Icon = item.icon;

          if (item.kind === 'leaf') {
            const active = isPathActive(item.path);
            return (
              <button key={item.id} data-testid={item.testid}
                onClick={() => nav(item.path)}
                className={`side-item ${active ? 'active' : ''}`}>
                <span className="truncate flex-1 text-right">{item.label}</span>
                <span className="side-ic"><Icon size={15} strokeWidth={1.75} /></span>
              </button>
            );
          }

          const isOpen = openId === item.id;
          const sectionActive = item.children?.some((c) => isPathActive(c.path));

          return (
            <div key={item.id} className="space-y-1">
              <button data-testid={item.testid} onClick={() => toggleSection(item.id)} aria-expanded={isOpen}
                className={`side-item ${sectionActive ? 'active' : ''}`}>
                <ChevronDown size={13} strokeWidth={2}
                  className={`text-slate-500 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                <span className="truncate flex-1 text-right">{item.label}</span>
                <span className="side-ic"><Icon size={15} strokeWidth={1.75} /></span>
              </button>

              {isOpen && (
                <div className="fade-in space-y-0.5 mr-2 border-r hair pr-1">
                  {item.children.map((c) => {
                    const CIcon = c.icon;
                    const cActive = isPathActive(c.path);
                    return (
                      <button key={c.id} data-testid={c.testid}
                        onClick={() => nav(c.path)}
                        className={`side-sub w-full ${cActive ? 'active' : ''}`}>
                        <CIcon size={13} strokeWidth={1.75} />
                        <span className="truncate flex-1 text-right">{c.label}</span>
                        {c.badge && <span className="pill pill-purple text-[9px] px-1.5 py-0">{c.badge}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer utilities */}
      <div className="mt-3 pt-3 border-t hair space-y-1">
        <div className="text-[10.5px] text-slate-500 text-center py-2">
          <span className="opacity-70">TBC Supply Operations</span>
          <br />
          <span className="opacity-40">v6.3 · Feb 2026</span>
        </div>
      </div>
    </aside>
  );
}
